import { whenCalm } from './whenCalm'

/**
 * Ruft `run`, sobald eine Seite **fertig geladen** ist: `measure()` liefert `stableFrames` Bilder
 * lang denselben Stand — nichts wächst mehr, nichts rutscht —, danach hat der Main-Thread Luft
 * (`whenCalm`). `measure` gibt `null`, solange das Ziel noch fehlt.
 *
 * Nach `maxWait` ms läuft `run` trotzdem, wenn das Ziel da ist; fehlt es dann noch, gibt es auf.
 *
 * Für Sprünge zu einer Aufgabe (#210, „Favoriten“): Demnächst lädt nach dem Öffnen noch Monate
 * nach, eine Seite zeichnet in mehreren Takten. Eine Aufgabe, die mitten darin ausgewählt und ins
 * Bild gerollt wird, rutscht wieder weg (gemeldet 01.10.2026).
 *
 * Gibt eine Abbruch-Funktion zurück.
 *
 * @example
 * useEffect(() => whenSettled(() => measureRow(taskId), () => select(taskId)), [taskId])
 */
export function whenSettled(
  measure: () => string | null,
  run: () => void,
  {
    stableFrames = 6,
    maxWait = 4000,
    calm = whenCalm,
    requestFrame = (cb: FrameRequestCallback) => requestAnimationFrame(cb),
    cancelFrame = (id: number) => cancelAnimationFrame(id),
    now = () => performance.now(),
  }: {
    stableFrames?: number
    maxWait?: number
    calm?: (run: () => void) => () => void
    requestFrame?: (cb: FrameRequestCallback) => number
    cancelFrame?: (id: number) => void
    now?: () => number
  } = {},
): () => void {
  const started = now()
  let frame = 0
  let still = 0
  let last: string | null = null
  let cancelCalm: (() => void) | undefined
  let cancelled = false

  const tick = () => {
    if (cancelled) return
    const state = measure()
    still = state !== null && state === last ? still + 1 : 0
    last = state
    if (still >= stableFrames || (state !== null && now() - started >= maxWait)) {
      cancelCalm = calm(run)
      return
    }
    if (now() - started >= maxWait) return
    frame = requestFrame(tick)
  }
  frame = requestFrame(tick)

  return () => {
    cancelled = true
    cancelFrame(frame)
    cancelCalm?.()
  }
}
